import React, { memo, useRef } from 'react';
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop, XYCoord } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import DraggableListItem from 'bringg-web/components/draggable-list-item/draggable-list-item';

export interface DraggableItemProps {
	checked: boolean;
	dragDisabled: boolean;
	index: number;
	column: { id: string; translateKey: string };
	moveItem: (dragId: string, hoverIndex: number) => void;
	onChange: (event) => void;
	checkboxDisabled?: boolean;
	onDropItem?: (item: any, monitor: DragSourceMonitor) => void;
}

interface DragItem {
	index: number;
	id: string;
	type: string;
}

enum ItemTypes {
	ITEM = 'item'
}

const DraggableItem: React.FC<DraggableItemProps> = ({
	column,
	index,
	checked,
	onDropItem,
	moveItem,
	dragDisabled,
	onChange,
	checkboxDisabled
}: DraggableItemProps) => {
	const { t } = useTranslation();
	const { id } = column;

	const ref = useRef<HTMLDivElement>(null);
	const [{ handlerId }, drop] = useDrop({
		accept: ItemTypes.ITEM,
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId()
			};
		},
		hover(item: DragItem, monitor: DropTargetMonitor) {
			if (dragDisabled) {
				return;
			}
			if (!ref.current) {
				return;
			}

			const dragIndex = item.index;
			const hoverIndex = index;

			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}

			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();

			// Get vertical middle
			// eslint-disable-next-line @typescript-eslint/no-magic-numbers
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

			// Determine mouse position
			const clientOffset = monitor.getClientOffset();

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			// Time to actually perform the action
			moveItem(item.id, hoverIndex);

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			Object.assign(item, { index: hoverIndex });
		}
	});

	const [{ isDragging }, drag] = useDrag({
		type: ItemTypes.ITEM,
		item: () => ({ id, index }),
		collect: monitor => ({
			isDragging: monitor.isDragging()
		}),
		canDrag: () => !dragDisabled,
		// Moved drop handler from useDrop here to update state upon drop out of drop container
		end: (item, monitor) => onDropItem(item, monitor)
	});

	drag(drop(ref));

	return (
		<DraggableListItem
			ref={ref}
			checked={checked}
			dragDisabled={dragDisabled}
			id={column.id}
			title={t(column.translateKey)}
			onSelectCheckbox={onChange}
			checkboxDisabled={checkboxDisabled}
			isDragging={isDragging}
			handlerId={handlerId}
		/>
	);
};

export default memo(DraggableItem);
